// 标签式列表分页与路由解析（路径均来自当前 rootMenu 及其子菜单）
import { HOME_PATH } from '@/components/layout/utils/navMenu'
import { isPortalMenuVisible } from '@/components/portal/common/utils/portalMenuVisibility'
import {
  extractSegmentUnderRoot,
  getMenuNodePath,
  isPathUnderMenu,
  joinMenuPath,
  normalizeContentPath,
  normalizeMenuPath,
  resolveMenuNodePath,
  slugFromMenuPath
} from '@/components/portal/common/utils/menuPath'

export const NEWS_PAGE_SIZE = 5
export const NEWS_LOAD_MORE_SIZE = 4

/** 标签式栏目根路径（兼容 CMS 根 path 为空、仅子分类有 path 的情况） */
export function resolveFocusCarouselRootPath(rootMenu = null) {
  return resolveMenuNodePath(rootMenu)
}

function findNavItemByPath(navItems = [], path) {
  const target = normalizeContentPath(path)
  return (navItems || []).find((item) => normalizeMenuPath(item.path) === target) || null
}

function findNavItemByPathFromMenu(rootMenu, path) {
  if (!rootMenu) return null
  const target = normalizeContentPath(path)

  for (const child of (rootMenu.children || []).filter((item) => isPortalMenuVisible(item))) {
    const childPath = getMenuNodePath(child)
    if (!childPath || normalizeMenuPath(childPath) !== target) continue
    return {
      slug: slugFromMenuPath(child.path) || String(child.id),
      path: childPath
    }
  }

  return null
}

function resolveNavItemByPath(path, navItems = [], rootMenu = null) {
  return findNavItemByPath(navItems, path) || findNavItemByPathFromMenu(rootMenu, path)
}

function findNavItemBySlug(navItems = [], slug, rootMenu = null) {
  if (!slug) return null
  const fromNav = (navItems || []).find((item) => item.slug === slug)
  if (fromNav) return fromNav

  if (!rootMenu) return null
  for (const child of (rootMenu.children || []).filter((item) => isPortalMenuVisible(item))) {
    const childSlug = slugFromMenuPath(child.path) || String(child.id)
    if (childSlug !== slug) continue
    const childPath = getMenuNodePath(child)
    if (!childPath) continue
    return { slug: childSlug, path: childPath }
  }

  return null
}

/** 从菜单根路径下提取分类 slug */
export function extractNewsPathSlug(path, rootMenu = null) {
  return extractSegmentUnderRoot(path, resolveFocusCarouselRootPath(rootMenu))
}

/** 三级菜单分类 slug 列表 */
export function getNewsCategorySlugs(navItems = []) {
  return (navItems || [])
    .filter((item) => item?.slug)
    .map((item) => item.slug)
}

/** 默认列表路径（第一个子菜单） */
export function getDefaultNewsListPath(navItems = [], rootMenu = null) {
  const first = (navItems || []).find((item) => item?.path || item?.slug)
  if (first?.path) return first.path

  const firstChild = (rootMenu?.children || []).find((item) => {
    if (!isPortalMenuVisible(item)) return false
    return Boolean(getMenuNodePath(item))
  })
  return firstChild ? getMenuNodePath(firstChild) : ''
}

/** 判断路径是否为列表页（当前 rootMenu 下的分类 Tab 页） */
export function isNewsListPath(path, navItems = [], rootMenu = null) {
  const item = resolveNavItemByPath(path, navItems, rootMenu)
  if (!item?.slug) return false
  if (!rootMenu) return true

  const rootPath = resolveFocusCarouselRootPath(rootMenu)
  if (!rootPath) return true
  return isPathUnderMenu(path, rootPath)
}

/** 判断路径是否属于当前 rootMenu 管辖范围 */
export function isFocusCarouselRootPath(path, rootMenu = null) {
  if (!rootMenu) return false
  const rootPath = resolveFocusCarouselRootPath(rootMenu)
  if (!rootPath) return false
  return isPathUnderMenu(path, rootPath)
}

/** 判断路径是否为栏目根路径（需重定向到默认子页） */
export function isMenuRootPath(path, rootMenu = null) {
  const rootPath = resolveFocusCarouselRootPath(rootMenu)
  if (!rootPath) return false
  return normalizeContentPath(path) === rootPath
}

/** 根据当前路由解析内容请求参数 */
export function resolveNewsFetchPayload(path, options = {}) {
  const navItems = options.navItems || []
  const rootMenu = options.rootMenu || null
  const category = getCategoryFromPath(path, navItems, rootMenu)
  if (!category) return null
  return { category }
}

/** 根据请求 payload 生成 Vuex loading key */
export function getNewsSectionLoadingKey(payload = {}) {
  return payload.category || 'default'
}

/** 从路由路径提取分类 slug */
export function getCategoryFromPath(path, navItems = [], rootMenu = null) {
  const item = resolveNavItemByPath(path, navItems, rootMenu)
  if (!item?.slug) return ''
  if (!rootMenu) return item.slug

  const rootPath = resolveFocusCarouselRootPath(rootMenu)
  if (!rootPath) return item.slug
  if (!isPathUnderMenu(path, rootPath)) return ''
  return item.slug
}

/** 判断 slug 是否为分类保留字（非详情页 slug） */
export function isReservedNewsSlug(slug, navItems = [], rootMenu = null) {
  if (!slug) return false
  const item = findNavItemBySlug(navItems, slug, rootMenu)
  return Boolean(item?.slug)
}

/** 从路径解析详情 slug（非分类列表页） */
export function extractNewsDetailSlug(path, navItems = [], rootMenu = null) {
  const slug = extractNewsPathSlug(path, rootMenu)
  if (!slug || isNewsListPath(path, navItems, rootMenu) || isReservedNewsSlug(slug, navItems, rootMenu)) {
    return ''
  }
  return slug
}

/** 解析详情页链接 */
export function buildNewsDetailPath(item, rootMenu = null) {
  if (!item) return ''
  const base = resolveFocusCarouselRootPath(rootMenu)
  const routeKey = String(item.id || item.slug || '')
  // 根 path 无效或等于首页时不能拼详情，否则会落到 /:id 或空链接
  if (!base || base === HOME_PATH || !routeKey || routeKey === 'undefined') return ''
  return joinMenuPath(base, routeKey)
}

/** 解析列表分类页链接 */
export function buildNewsCategoryPath(category, navItems = [], rootMenu = null) {
  const item = findNavItemBySlug(navItems, category, rootMenu)
  return item?.path || ''
}

/** 栏目根路径重定向到首个可导航子路径（不返回 root 自身，避免重定向死循环） */
export function resolveRootRedirectPath(rootMenu = null, navItems = []) {
  const rootPath = normalizeMenuPath(resolveFocusCarouselRootPath(rootMenu))
  const defaultList = normalizeMenuPath(getDefaultNewsListPath(navItems, rootMenu))

  if (defaultList && defaultList !== rootPath) {
    return defaultList
  }

  for (const child of (rootMenu?.children || []).filter((item) => isPortalMenuVisible(item))) {
    const childPath = normalizeMenuPath(getMenuNodePath(child))
    if (childPath && childPath !== rootPath) {
      return childPath
    }
  }

  return ''
}

/** 按可见数量切分新闻列表：首条 featured + 其余 grid + 是否还有更多 */
export function getVisibleNews(items, visibleCount) {
  const count = Math.min(visibleCount, items.length)

  return {
    featured: items[0] || null,
    gridItems: items.slice(1, count),
    hasMore: count < items.length,
    total: items.length
  }
}
